import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jobsAPI, settingsAPI } from '../services/api';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';
import { toast } from 'react-hot-toast';
import { Calendar, Save, Send, Edit, X, Clock, AlertCircle } from 'lucide-react';

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const PostJob: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [isEdit, setIsEdit] = useState(!!editId);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    responsibilities: '',
    skills: '',
    country: '',
    city: '',
    remote: false,
    employmentType: 'full-time',
    experienceLevel: '',
    minSalary: '',
    maxSalary: '',
    currency: 'USD',
    negotiable: false,
    interviewRequired: true,
    applicationDeadline: '',
    status: 'draft',
  });
  
  const [currencies, setCurrencies] = useState<Currency[]>([
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
    { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
    { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw' },
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingJob, setFetchingJob] = useState(!!editId);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrencies();
    if (editId) {
      fetchJob(editId);
    }
  }, [editId]);

  const fetchCurrencies = async () => {
    try {
      const response = await settingsAPI.getCurrencies();
      if (response.data?.data) {
        setCurrencies(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch currencies:', err);
    }
  };

  const fetchJob = async (id: string) => {
    try {
      const response = await jobsAPI.getById(id);
      const job = response.data;
      setFormData({
        title: job.title || '',
        description: job.description || '',
        requirements: (job.requirements || []).join('\n'),
        responsibilities: (job.responsibilities || []).join('\n'),
        skills: (job.skills || []).join(', '),
        country: job.location?.country || '',
        city: job.location?.city || '',
        remote: job.location?.remote || false,
        employmentType: job.employmentType || 'full-time',
        experienceLevel: job.experienceLevel || '',
        minSalary: job.salary?.min?.toString() || '',
        maxSalary: job.salary?.max?.toString() || '',
        currency: job.salary?.currency || 'USD',
        negotiable: job.salary?.negotiable || false,
        interviewRequired: job.interviewRequired !== false,
        applicationDeadline: job.applicationDeadline ? new Date(job.applicationDeadline).toISOString().split('T')[0] : '',
        status: job.status || 'draft',
      });
    } catch (err: any) {
      toast.error('Failed to fetch job details');
      navigate('/employer/jobs');
    } finally {
      setFetchingJob(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const jobData = {
        title: formData.title,
        description: formData.description,
        requirements: formData.requirements.split('\n').filter(r => r.trim()),
        responsibilities: formData.responsibilities.split('\n').filter(r => r.trim()),
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s),
        location: {
          country: formData.country,
          city: formData.city,
          remote: formData.remote,
        },
        employmentType: formData.employmentType,
        experienceLevel: formData.experienceLevel || undefined,
        salary: formData.minSalary ? {
          min: Number(formData.minSalary),
          max: Number(formData.maxSalary),
          currency: formData.currency,
          negotiable: formData.negotiable,
        } : undefined,
        interviewRequired: formData.interviewRequired,
        applicationDeadline: formData.applicationDeadline ? new Date(formData.applicationDeadline) : undefined,
        status: formData.status,
      };

      if (isEdit && editId) {
        await jobsAPI.update(editId, jobData);
        toast.success('Job updated successfully');
      } else {
        await jobsAPI.create(jobData);
        toast.success(formData.status === 'draft' ? 'Job saved as draft' : 'Job posted successfully');
      }
      navigate('/employer/jobs');
    } catch (err: any) {
      const message = err.response?.data?.message || '';
      if (message.includes('not found') || message.includes('profile')) {
        setError('Please complete your business profile first before posting jobs.');
      } else if (message.includes('unauthorized') || message.includes('not authorized')) {
        setError('Please login as a business account to post jobs.');
      } else {
        setError(message || 'Failed to post job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setFormData({ ...formData, status: 'draft' });
    const formSubmitEvent = new Event('submit') as any;
    formSubmitEvent.preventDefault = () => {};
    await handleSubmit(formSubmitEvent);
  };

  const handlePublish = async () => {
    setFormData({ ...formData, status: 'active' });
    const formSubmitEvent = new Event('submit') as any;
    formSubmitEvent.preventDefault = () => {};
    await handleSubmit(formSubmitEvent);
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (fetchingJob) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary mb-2">
            {isEdit ? 'Edit Job' : 'Post a Job'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Update your job posting details' : 'Fill in the details to create a job posting'}
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Status Banner */}
            {isEdit && (
              <div className={`rounded-lg p-4 flex items-center justify-between ${
                formData.status === 'active' ? 'bg-green-50 border border-green-200' :
                formData.status === 'draft' ? 'bg-yellow-50 border border-yellow-200' :
                formData.status === 'closed' ? 'bg-gray-50 border border-gray-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    formData.status === 'active' ? 'bg-green-100 text-green-800' :
                    formData.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                    formData.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
                  </span>
                  {formData.status === 'draft' && (
                    <span className="text-sm text-yellow-700">This job is not visible to job seekers</span>
                  )}
                  {formData.status === 'closed' && (
                    <span className="text-sm text-gray-700">This job is no longer accepting applications</span>
                  )}
                  {formData.status === 'active' && (
                    <span className="text-sm text-green-700">This job is visible to job seekers</span>
                  )}
                </div>
                {formData.status === 'closed' && (
                  <Button type="button" variant="outline" onClick={() => setFormData({ ...formData, status: 'active' })}>
                    Reopen Job
                  </Button>
                )}
              </div>
            )}

            <Input
              label="Job Title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Senior Software Engineer"
              required
              icon="fas fa-briefcase"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the role and what you're looking for..."
                required
                rows={5}
                className="input-field"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements (one per line)
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  placeholder="Bachelor's degree in Computer Science&#10;5+ years experience&#10;Strong communication skills"
                  rows={4}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Responsibilities (one per line)
                </label>
                <textarea
                  name="responsibilities"
                  value={formData.responsibilities}
                  onChange={handleChange}
                  placeholder="Design and develop features&#10;Code reviews&#10;Mentor junior developers"
                  rows={4}
                  className="input-field"
                />
              </div>
            </div>

            <Input
              label="Skills (comma separated)"
              type="text"
              name="skills"
              value={formData.skills}
              onChange={handleChange}
              placeholder="JavaScript, React, Node.js, MongoDB"
              icon="fas fa-code"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Country"
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Kenya"
                icon="fas fa-globe"
              />

              <Input
                label="City"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Nairobi"
                icon="fas fa-map-marker-alt"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="remote"
                checked={formData.remote}
                onChange={handleChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Remote work available</span>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="interviewRequired"
                  id="interviewRequired"
                  checked={formData.interviewRequired}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="interviewRequired" className="ml-2 text-sm text-gray-700">
                  <strong>Interview required</strong> - Uncheck if you want to hire candidates directly without interview
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                If unchecked, you can hire candidates directly from applications without scheduling an interview
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="employmentType"
                  value={formData.employmentType}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                  <option value="freelance">Freelance</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Select level</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-secondary mb-4">Salary Information (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Salary
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="minSalary"
                      value={formData.minSalary}
                      onChange={handleChange}
                      placeholder="50000"
                      disabled={formData.negotiable}
                      className={`input-field pr-10 ${formData.negotiable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {currencies.find(c => c.code === formData.currency)?.symbol || '$'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Salary
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="maxSalary"
                      value={formData.maxSalary}
                      onChange={handleChange}
                      placeholder="80000"
                      disabled={formData.negotiable}
                      className={`input-field pr-10 ${formData.negotiable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {currencies.find(c => c.code === formData.currency)?.symbol || '$'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.code} - {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  name="negotiable"
                  id="negotiable"
                  checked={formData.negotiable}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="negotiable" className="ml-2 text-sm text-gray-700">
                  Salary is negotiable
                </label>
              </div>
              {formData.negotiable && (
                <p className="text-xs text-blue-600 mt-1 ml-6">
                  Salary range will be hidden. Candidates will need to apply to learn more.
                </p>
              )}
            </div>

            {/* Application Deadline */}
            <div>
              <h3 className="text-lg font-semibold text-secondary mb-4">Application Deadline</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deadline Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      name="applicationDeadline"
                      value={formData.applicationDeadline}
                      onChange={handleChange}
                      min={getMinDate()}
                      className="input-field pl-10"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty if there's no deadline
                  </p>
                </div>
                
                {formData.applicationDeadline && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        Auto-close enabled
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Job will automatically close on {new Date(formData.applicationDeadline).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Job Status (only for edit) */}
            {isEdit && (
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-4">Job Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['draft', 'active', 'closed'].map(status => (
                    <label
                      key={status}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.status === status
                          ? status === 'active' ? 'border-green-500 bg-green-50' :
                            status === 'draft' ? 'border-yellow-500 bg-yellow-50' :
                            'border-gray-500 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={formData.status === status}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <p className="font-medium capitalize text-gray-900">{status}</p>
                        <p className="text-xs text-gray-500">
                          {status === 'draft' && 'Not visible to job seekers'}
                          {status === 'active' && 'Visible and accepting applications'}
                          {status === 'closed' && 'No longer accepting applications'}
                        </p>
                      </div>
                      {formData.status === status && (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          status === 'active' ? 'bg-green-500' :
                          status === 'draft' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`}>
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded flex items-center gap-2">
              <i className="fas fa-info-circle"></i>
              <span>
                <strong>Note:</strong> You must be logged in as a business account and complete your business profile before posting jobs.
                {formData.status === 'draft' && " This job will be saved as a draft and won't be visible until you publish it."}
              </span>
            </div>

            <div className="flex flex-wrap gap-4">
              {isEdit ? (
                <>
                  <Button type="submit" variant="primary" loading={loading}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  {formData.status === 'draft' && (
                    <Button type="button" variant="secondary" onClick={handlePublish} loading={loading}>
                      <Send className="w-4 h-4 mr-2" />
                      Publish Job
                    </Button>
                  )}
                  {formData.status === 'active' && (
                    <Button type="button" variant="outline" onClick={() => setFormData({ ...formData, status: 'closed' })}>
                      <X className="w-4 h-4 mr-2" />
                      Close Job
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={handleSaveDraft} loading={loading}>
                    <Save className="w-4 h-4 mr-2" />
                    Save as Draft
                  </Button>
                  <Button type="submit" variant="primary" loading={loading}>
                    <Send className="w-4 h-4 mr-2" />
                    Post Job
                  </Button>
                </>
              )}
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PostJob;
